/**
 * F128 (Pavel 2026-08-11): the owner role is SET AUTOMATICALLY, and a failure
 * has a name.
 *
 * The law, verbatim: "má se to automaticky spouštět číslem, který napsal klient
 * do políčka, a nemáš řešit tu pojistku. Nemá tam být lidský krok parťáka...
 * A jedině v momentě, kdy nastane nějaká chyba, tak upozornit parťáka včetně
 * toho, jaká chyba nastala, co bot odpověděl. Když tam napíšu nějaký nesmysl a
 * odpověď je, že to není validní číslo, tak to by se mělo vrátit klidně i přímo
 * klientovi, aby to mohl napsat znova."
 *
 * This module is the ONE catalog of `/setProOwner` failure names, the
 * `shared/bot-transfer-reasons.ts` shape one lane over: the worker decides the
 * AUDIENCE from it, handover step 2 decides whether to offer the form again
 * from it, the Servis queue labels the row from it, and
 * `buddypro-instance/launchpad_owner_role.py` mirrors it in Python. A reason
 * that is client-fault on one side and internal on the other is the F112 bug in
 * a different costume.
 *
 * 🔴 The names below are derived from the platform's own five constant results
 * (`telegram/Invoked Functions/replyToUser.ts`, the `/setProOwner` branch), not
 * from guesswork:
 *   "Success"
 *   "Error, user does not exist. You need to invite them first. ..."
 *   "Error: The new owner id is not in correct format, must be for example: ..."
 *   "Error: This command is allowed only inside BuddyPro when you are its owner."
 *   "Error"
 *
 * 🔴 And the caveat that shapes the whole lane: the instance does NOT send
 * those strings back. It hands them to its own model as `<COMMAND-RESULT>` with
 * the instruction "explain this command result with your own words", so what
 * the runner reads is PROSE, in the instance's language. The text therefore
 * NAMES the cause; it is never, on its own, permission to call something done
 * (the F113 law). A reply the runner cannot place lands on
 * `verification_unavailable`, which is internal and keeps the job open.
 */

/**
 * The client can act on these. Both are about the NUMBER the client typed into
 * the form, which is the only part of this move they control: handover step 2
 * names the reason and opens the form again (the F44/F112 client_fix shape).
 */
export const OWNER_ROLE_CLIENT_REASONS = [
  // "Error, user does not exist. You need to invite them first." - the number
  // is not a user of this instance yet. Only the client can fix that: they open
  // the bot, or they give us the right ID.
  "owner_not_member",
  // The stored ID is not a Telegram numeric id at all. Refused by the runner
  // BEFORE the command goes out (the F112 "answerable without Telegram" move),
  // so a nonsense value costs no round trip and no partner's attention.
  "invalid_owner_id"
] as const;

/**
 * Ours to solve. The client keeps the honest "nastavujeme" and the partner gets
 * the F94 notification with the client's name in the title AND the instance's
 * own answer quoted verbatim (F112 providerMessage).
 */
export const OWNER_ROLE_INTERNAL_REASONS = [
  // "Error: This command is allowed only inside BuddyPro when you are its
  // owner." - we sent it to the wrong bot, or our pool account is not the owner
  // of this instance any more. Nothing the client did.
  "not_our_instance",
  // "Error: The new owner id is not in correct format ..." - the platform
  // checks that the value ends with `#<botUsername>`, and the runner has
  // already proved the digits. So a format refusal is about the suffix WE
  // composed, and charging the client for it would be F112 in reverse.
  "id_format_rejected",
  // The bare "Error": the platform's own write (re-encrypting the instance's
  // API keys under the new owner) did not go through.
  "platform_error",
  // No authoritative session for this bot, or Telegram refused us.
  "session_unavailable",
  // 🔴 The command went out and the answer could not be read, or could not be
  // placed. NEVER done: the job stays open, the client keeps the honest wait
  // and a human looks. There is deliberately no reason code for "the reply
  // sounded positive".
  "verification_unavailable",
  "unknown"
] as const;

export const OWNER_ROLE_REASONS = [
  ...OWNER_ROLE_CLIENT_REASONS,
  ...OWNER_ROLE_INTERNAL_REASONS
] as const;

export type OwnerRoleClientReason = (typeof OWNER_ROLE_CLIENT_REASONS)[number];
export type OwnerRoleInternalReason = (typeof OWNER_ROLE_INTERNAL_REASONS)[number];
export type OwnerRoleReason = OwnerRoleClientReason | OwnerRoleInternalReason;

export function isOwnerRoleClientFault(reason: string): reason is OwnerRoleClientReason {
  return (OWNER_ROLE_CLIENT_REASONS as readonly string[]).includes(reason);
}

export function isOwnerRoleReason(reason: string): reason is OwnerRoleReason {
  return (OWNER_ROLE_REASONS as readonly string[]).includes(reason);
}

/**
 * The one locale key shape for a reason, read by the client's step, the
 * partner's notification and the Servis queue. An unknown value falls back
 * rather than throwing: `translate` THROWS on a missing key, and a reason the
 * copy does not know must not take a whole panel down (the F113 lesson about
 * `mail.botTransferReason`).
 */
export function ownerRoleReasonKey(reason: string): string {
  return `ownerRole.reason.${isOwnerRoleReason(reason) ? reason : "unknown"}`;
}

/**
 * The client fix note stored on `handover_telegram.$.owner_role_fix`.
 *
 * It lives on the SAME resource as the ID it is about, exactly like the
 * `owner_role_set_at` fact (F118): the note is a statement about that number,
 * so a corrected number must not inherit it. `handoverTelegramValue` rebuilds
 * the value on every submit and carries only the role fact, which is what makes
 * the note disappear the moment the client sends a new ID.
 */
export interface OwnerRoleFix {
  reason: OwnerRoleReason;
  at: string;
}

export function readOwnerRoleFix(value: Record<string, unknown> | null | undefined): OwnerRoleFix | null {
  const raw = value?.owner_role_fix;
  if (!raw || typeof raw !== "object") return null;
  const record = raw as Record<string, unknown>;
  const reason = typeof record.reason === "string" ? record.reason : "";
  const at = typeof record.at === "string" ? record.at : "";
  if (!isOwnerRoleReason(reason) || !isOwnerRoleClientFault(reason) || !at) return null;
  return { reason, at };
}
